import { useMemo, useState } from "react";
import { toast } from "sonner";

import { RestoreYamlButton } from "@/components/sources/restore-yaml-button";
import { VendorGroup } from "@/components/sources/vendor-group";
import { useRunSource, useSources } from "@/hooks/useSources";
import type { SourceConfig } from "@/lib/api";

export default function SourcesPage() {
  const sourcesQ = useSources();
  const runSource = useRunSource();
  const [runningId, setRunningId] = useState<string | null>(null);

  const onRun = async (sourceId: string) => {
    setRunningId(sourceId);
    try {
      const resp = await runSource.mutateAsync(sourceId);
      const count = resp.inbox_items_created;
      toast.success(
        `${resp.vendor}: ${count} inbox item${count === 1 ? "" : "s"} captured`,
      );
    } catch (err) {
      toast.error(`Run failed: ${(err as Error).message}`);
    } finally {
      setRunningId(null);
    }
  };

  const sources = sourcesQ.data ?? [];

  // Group by vendor so sources that scrape different pages of the same
  // vendor cluster under one heading.
  // under one heading. Vendors without a `vendor` string land in a
  // "misc" bucket.
  const groups = useMemo(() => groupByVendor(sources), [sources]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-baseline justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold">Sources</h1>
          <p className="text-xs text-muted-fg">
            Configured vendor pages that feed the Signal Inbox. Use Ops for
            schedule, policy, and scrape history; YAML is backup and restore.
          </p>
        </div>
        <RestoreYamlButton />
      </div>

      <div className="flex-1 overflow-auto p-6">
        {sourcesQ.isLoading ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-fg">
            Loading sources…
          </div>
        ) : sourcesQ.isError ? (
          <div className="flex h-40 items-center justify-center text-sm text-red-400">
            Failed to load sources: {(sourcesQ.error as Error).message}
          </div>
        ) : sources.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-fg">
            No sources configured. Add sources here or restore a YAML backup.
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {groups.map(({ vendor, items }) => (
              <VendorGroup
                key={vendor}
                vendor={vendor}
                sources={items}
                onRun={onRun}
                runningId={runningId}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Stable bucketing by vendor; fallback bucket 'misc' for sources
 *  without a vendor string (manual_source, webhook-only entries). */
function groupByVendor(sources: SourceConfig[]): { vendor: string; items: SourceConfig[] }[] {
  const map = new Map<string, SourceConfig[]>();
  for (const s of sources) {
    const key = s.vendor ?? "misc";
    const list = map.get(key);
    if (list) list.push(s);
    else map.set(key, [s]);
  }
  return Array.from(map.entries())
    .map(([vendor, items]) => ({ vendor, items }))
    .sort((a, b) => a.vendor.localeCompare(b.vendor));
}
